import { FilterType } from 'src/app/components/lib/wizard/filters';

import { ThresholdType, TotalItemConditionType } from '../../models';
import { FILTER_RESOURCES } from '../shared/constants';

import type { FilterResource, FilterResourceType } from '../shared/filter';

export enum RequirementsStepActions {
  ADD_REQUIREMENT = 'add-requirement',
  DELETE_REQUIREMENT = 'delete-requirement',
  INITIALIZE = 'initialize',
  RESET = 'reset',
  SET_ACTIVE_FILTER = 'set-active-filter',
  SET_ALLOW_NON_CANNABIS_ITEMS = 'set-allow-non-cannabis-items',
  SET_REQUIRE_SAME_PRODUCT = 'set-require-same-product',
  SET_REQUIREMENT_CONDITION = 'set-requirement-condition',
  SET_REQUIREMENT_FILTER = 'set-requirement-filter',
  SET_REQUIREMENT_MAXIMUM_ITEMS = 'set-requirement-maximum-items',
  SET_REQUIREMENT_MINIMUM_GRAMS = 'set-requirement-minimum-grams',
  SET_REQUIREMENT_MINIMUM_ITEMS = 'set-requirement-minimum-items',
  SET_REQUIREMENT_MINIMUM_SPEND = 'set-requirement-minimum-spend',
  SET_REQUIREMENT_TOTAL_ITEM_CONDITION = 'set-requirement-total-condition',
  VISITED = 'set-visited',
}

type PayloadWithIndex<T> = T & {
  index: number;
};

type AddRequirementAction = {
  type: RequirementsStepActions.ADD_REQUIREMENT;
};

type DeleteRequirementAction = {
  payload: { index: number };
  type: RequirementsStepActions.DELETE_REQUIREMENT;
};

type InitializeAction = {
  payload: RequirementsStepState;
  type: RequirementsStepActions.INITIALIZE;
};

type ResetAction = {
  type: RequirementsStepActions.RESET;
};

type SetActiveFilter = {
  payload: { filter: FilterResourceType | undefined; requirement: number | undefined };
  type: RequirementsStepActions.SET_ACTIVE_FILTER;
};

type SetAllowNonCannabisItemsAction = {
  payload: PayloadWithIndex<{ value: Requirement['allowNonCannabisItems'] }>;
  type: RequirementsStepActions.SET_ALLOW_NON_CANNABIS_ITEMS;
};

type SetRequireSameProductAction = {
  payload: PayloadWithIndex<{ value: Requirement['requireSameProduct'] }>;
  type: RequirementsStepActions.SET_REQUIRE_SAME_PRODUCT;
};

type SetRequirementConditionAction = {
  payload: PayloadWithIndex<{ value: Requirement['condition'] }>;
  type: RequirementsStepActions.SET_REQUIREMENT_CONDITION;
};

type SetRequirementFilterAction = {
  payload: PayloadWithIndex<{
    value: {
      resources: FilterResource[];
      resourceType: FilterResourceType;
      selectionType: FilterType;
    };
  }>;
  type: RequirementsStepActions.SET_REQUIREMENT_FILTER;
};

type SetRequirementMaxmimumItemsAction = {
  payload: PayloadWithIndex<{ value: number | '' }>;
  type: RequirementsStepActions.SET_REQUIREMENT_MAXIMUM_ITEMS;
};

type SetRequirementMinimumGramsAction = {
  payload: PayloadWithIndex<{ value: number | '' }>;
  type: RequirementsStepActions.SET_REQUIREMENT_MINIMUM_GRAMS;
};

type SetRequirementMinimumItemsAction = {
  payload: PayloadWithIndex<{ value: number | '' }>;
  type: RequirementsStepActions.SET_REQUIREMENT_MINIMUM_ITEMS;
};

type SetRequirementMinimumSpendAction = {
  payload: PayloadWithIndex<{ value: number | '' }>;
  type: RequirementsStepActions.SET_REQUIREMENT_MINIMUM_SPEND;
};

type SetRequirementTotalConditionAction = {
  payload: PayloadWithIndex<{ value: TotalItemConditionType }>;
  type: RequirementsStepActions.SET_REQUIREMENT_TOTAL_ITEM_CONDITION;
};

type SetVisitedAction = {
  type: RequirementsStepActions.VISITED;
};

export type RequirementsStepAction =
  | AddRequirementAction
  | DeleteRequirementAction
  | InitializeAction
  | ResetAction
  | SetActiveFilter
  | SetAllowNonCannabisItemsAction
  | SetRequirementConditionAction
  | SetRequirementFilterAction
  | SetRequirementMaxmimumItemsAction
  | SetRequirementMinimumGramsAction
  | SetRequirementMinimumItemsAction
  | SetRequirementMinimumSpendAction
  | SetRequirementTotalConditionAction
  | SetRequireSameProductAction
  | SetVisitedAction;

type ActionWithPayload =
  | SetAllowNonCannabisItemsAction
  | SetRequirementConditionAction
  | SetRequirementFilterAction
  | SetRequirementMaxmimumItemsAction
  | SetRequirementMinimumGramsAction
  | SetRequirementMinimumItemsAction
  | SetRequirementMinimumSpendAction
  | SetRequirementTotalConditionAction
  | SetRequireSameProductAction;

export type Filter = { resources: FilterResource[]; selectionType: FilterType };

export type Filters = Record<FilterResourceType, Filter>;

export type Requirement = {
  allowNonCannabisItems: boolean;
  condition: ThresholdType;
  filters: Filters;
  id: number | null;
  maximumItems: number | '';
  minimumGrams: number | '';
  minimumItems: number | '';
  minimumSpend: number | '';
  requireSameProduct: boolean;
  totalItemCondition: TotalItemConditionType;
};

export type RequirementsStepState = Record<number, Requirement> & {
  activeFilter: {
    filter: FilterResourceType | undefined;
    requirement: number | undefined;
  };
  visited: boolean;
};

const defaultFilter: Filter = { selectionType: FilterType.NONE, resources: [] };

export const defaultFilters = Object.keys(FILTER_RESOURCES).reduce(
  (accum, key) => ({
    ...accum,
    [FILTER_RESOURCES[key]]: { ...defaultFilter },
  }),
  {}
) as Filters;

export const defaultRequirementState: Requirement = {
  condition: ThresholdType.NoThreshold,
  filters: { ...defaultFilters },
  maximumItems: '',
  minimumGrams: '',
  minimumItems: '',
  minimumSpend: '',
  allowNonCannabisItems: true,
  requireSameProduct: false,
  totalItemCondition: TotalItemConditionType.AtLeast,
  id: null,
};

export const requirementsStepDefaultState: RequirementsStepState = {
  activeFilter: {
    requirement: undefined,
    filter: undefined,
  },
  visited: false,
  1: { ...defaultRequirementState },
};

export function requirementsStepReducer(state: RequirementsStepState, action: RequirementsStepAction) {
  switch (action.type) {
    case RequirementsStepActions.ADD_REQUIREMENT:
      return {
        ...state,
        /**
         *  If the step state was only a Record of requirements, the added index value would be `Object.keys(state).length + 1`
         *  As it were, we also have the `activeFilter` and `visited` keys.
         *  This means we need to subtract two to get the value to account for the two "extra" keys in the state object
         */
        [Object.keys(state).length - 1]: { ...defaultRequirementState, condition: ThresholdType.NumberOfItems },
      };
    case RequirementsStepActions.DELETE_REQUIREMENT:
      return deleteRequirementByIndexAndDecrement({ index: action.payload.index, state });
    case RequirementsStepActions.INITIALIZE:
      return {
        ...action.payload,
      };
    case RequirementsStepActions.RESET:
      return {
        ...requirementsStepDefaultState,
      };
    case RequirementsStepActions.SET_ACTIVE_FILTER:
      return {
        ...state,
        activeFilter: {
          ...action.payload,
        },
      };
    case RequirementsStepActions.SET_ALLOW_NON_CANNABIS_ITEMS:
      return setRequirementStateValueByIndex({
        action,
        state,
        key: 'allowNonCannabisItems',
      });
    case RequirementsStepActions.SET_REQUIRE_SAME_PRODUCT:
      return setRequirementStateValueByIndex({
        action,
        state,
        key: 'requireSameProduct',
      });
    case RequirementsStepActions.SET_REQUIREMENT_CONDITION:
      return setRequirementStateValueByIndex({
        action,
        state,
        key: 'condition',
      });
    case RequirementsStepActions.SET_REQUIREMENT_FILTER:
      return {
        ...state,
        activeFilter: {
          requirement: undefined,
          filter: undefined,
        },
        [action.payload.index]: {
          ...state[action.payload.index],
          filters: {
            ...state[action.payload.index].filters,
            [action.payload.value.resourceType]: {
              resources: action.payload.value.resources,
              selectionType: action.payload.value.selectionType,
            },
          },
        },
      };
    case RequirementsStepActions.SET_REQUIREMENT_MAXIMUM_ITEMS:
      return setRequirementStateValueByIndex({
        action,
        state,
        key: 'maximumItems',
      });
    case RequirementsStepActions.SET_REQUIREMENT_MINIMUM_GRAMS:
      return setRequirementStateValueByIndex({
        action,
        state,
        key: 'minimumGrams',
      });
    case RequirementsStepActions.SET_REQUIREMENT_MINIMUM_ITEMS:
      return setRequirementStateValueByIndex({
        action,
        state,
        key: 'minimumItems',
      });
    case RequirementsStepActions.SET_REQUIREMENT_MINIMUM_SPEND:
      return setRequirementStateValueByIndex({
        action,
        state,
        key: 'minimumSpend',
      });
    case RequirementsStepActions.SET_REQUIREMENT_TOTAL_ITEM_CONDITION:
      return setRequirementStateValueByIndex({
        action,
        state,
        key: 'totalItemCondition',
      });
    case RequirementsStepActions.VISITED:
      return {
        ...state,
        visited: true,
      };
    default:
      return state;
  }
}

type SetRequirementStateValueByIndexArg = {
  action: ActionWithPayload;
  key: keyof Requirement;
  state: RequirementsStepState;
};

function setRequirementStateValueByIndex({ action, state, key }: SetRequirementStateValueByIndexArg) {
  return {
    ...state,
    [action.payload.index]: {
      ...state[action.payload.index],
      [key]: action.payload.value,
    },
  };
}

function deleteRequirementByIndexAndDecrement({ index, state }: { index: number; state: RequirementsStepState }) {
  const { [index]: _, activeFilter, visited, ...rest } = state; // remove the deleted requirement
  const newState: RequirementsStepState = { activeFilter, visited };

  // add back each requirement
  // if the index is lower than the deleted requirement, the number remains the same
  // if the index is higher than the deleted requirement, decrement it
  Object.entries(rest).forEach(([currentIndex, requirement]) => {
    let indexAsNumber = Number(currentIndex);
    if (indexAsNumber > index) {
      indexAsNumber -= 1;
    }
    newState[indexAsNumber] = { ...requirement };
  });

  return newState;
}
